/**
 * LibrarySearchPage - Calil 統合検索による書誌検索画面
 *
 * ステップ:
 *   1. 検索フォームでフリーワード検索 → /calil/search を叩き書誌一覧を取得
 *   2. 本カードをタップするとカード内に所蔵館一覧を展開表示（展開時に ISBN 二次検索を実行）
 *   3. カードの☆ボタンでお気に入り登録・解除。登録した本は「候補」タブに並ぶ
 *   4. お気に入りの本は貸出ライフサイクル状態 (wishlist/reserved/returned) を切り替えられる
 *   5. 予約中は図書館ごとに表示し、確保メールで受取可になった館を先頭に出して一括で受取済みにできる
 *
 * region は saitama 固定 (Phase 1)。/calil/polling による全件ポーリングは
 * 廃止し、所蔵館の正確な情報はカード展開時の ISBN 二次検索でのみ取得する。
 *
 * レイアウト:
 * - 上部: SearchForm
 * - 中央: 上部タブ (検索結果 / 候補 / 予約中)。左右スワイプでも切り替えられる
 * - 履歴 (返却済み) は見る頻度が低いため、ヘッダーメニューからシートで開く
 * - 本カードの TODO ボタンで使うフォーマットも、ヘッダーメニューからシートで編集する
 */

import { BookPlus, ListPlus, Search } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import type { LibraryFavoriteStatus } from '@/api/types/library-favorite'
import { Header } from '@/components/shared/header'
import { SwipeTabs, type SwipeTabSpec } from '@/components/shared/swipe-tabs'
import { useQueryState } from '@/hooks/useQueryState'
import { PATHS } from '@/routes/paths'
import { useLibraryFavoritesStore } from '@/store/useLibraryFavoritesStore'
import { useLibraryFilterStore } from '@/store/useLibraryFilterStore'
import { useLibrarySearchStore } from '@/store/useLibrarySearchStore'

import { AmazonAddSheet } from './components/AmazonAddSheet'
import { BookTodoFormatSheet } from './components/BookTodoFormatSheet'
import { FavoriteBooksPanel } from './components/FavoriteBooksPanel'
import { HistorySheet } from './components/HistorySheet'
import { LibraryFilterSheet } from './components/LibraryFilterSheet'
import { LibrarySortSheet } from './components/LibrarySortSheet'
import { SearchForm } from './components/SearchForm'
import { SearchResultsPanel } from './components/SearchResultsPanel'
import { getFavoriteStatusMeta } from './lib/favorite-status'
import { type LibraryTab, libraryTabCodec } from './lib/library-tabs'

const TAB_ID_PREFIX = 'library-search-tabs'

const WISHLIST_META = getFavoriteStatusMeta('wishlist')
const RESERVED_META = getFavoriteStatusMeta('reserved')
const RETURNED_META = getFavoriteStatusMeta('returned')

export const LibrarySearchPage = () => {
  const navigate = useNavigate()

  const loadMapping = useLibrarySearchStore((s) => s.loadMapping)
  const status = useLibrarySearchStore((s) => s.status)
  const books = useLibrarySearchStore((s) => s.books)
  const expandedBookIds = useLibrarySearchStore((s) => s.expandedBookIds)
  const toggleBookExpansion = useLibrarySearchStore((s) => s.toggleBookExpansion)

  const loadFavorites = useLibraryFavoritesStore((s) => s.loadFavorites)
  const favoritesByIsbn = useLibraryFavoritesStore((s) => s.byIsbn)

  const loadFilter = useLibraryFilterStore((s) => s.loadFilter)
  const setSearchAllLibraries = useLibraryFilterStore((s) => s.setSearchAllLibraries)
  const [filterSheetOpen, setFilterSheetOpen] = useState(false)
  const [sortSheetOpen, setSortSheetOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [todoFormatOpen, setTodoFormatOpen] = useState(false)
  // タブ状態は URL クエリ (?tab=wishlist) で保持し、リロード・共有時に復元できるようにする
  const [activeTab, setActiveTab] = useQueryState('tab', libraryTabCodec)
  const [amazonOpen, setAmazonOpen] = useState(false)
  // 開くたびに key を変えてシートの入力と取得結果を作り直す
  const [amazonSeed, setAmazonSeed] = useState({ key: 0, url: '' })

  const openAmazonSheet = (url: string) => {
    setAmazonSeed((prev) => ({ key: prev.key + 1, url }))
    setAmazonOpen(true)
  }

  // 初回のみ図書館マッピング + お気に入り一覧 + 図書館フィルタ選択をロード
  useEffect(() => {
    void loadMapping()
    void loadFavorites()
    void loadFilter()
  }, [loadMapping, loadFavorites, loadFilter])

  // 全館検索は一時的な切り替えなので、画面を離れたらフィルタに戻す
  useEffect(() => () => setSearchAllLibraries(false), [setSearchAllLibraries])

  // 同じ本を再クリックしたら折りたたむ。複数本を同時に展開できる。
  const handleToggle = useCallback(
    (bookId: string) => {
      toggleBookExpansion(bookId)
    },
    [toggleBookExpansion],
  )

  const expandedBookIdSet = useMemo(() => new Set(expandedBookIds), [expandedBookIds])

  // タブと履歴メニューの件数表示用: byIsbn から状態別に集計する
  const favoriteCountByStatus = useMemo(() => {
    const counts: Record<LibraryFavoriteStatus, number> = { wishlist: 0, reserved: 0, returned: 0 }
    for (const fav of Object.values(favoritesByIsbn)) {
      counts[fav.status] += 1
    }
    return counts
  }, [favoritesByIsbn])

  const tabs = [
    {
      value: 'search',
      label: '検索結果',
      icon: Search,
      count: status === 'done' && books.length > 0 ? books.length : null,
    },
    {
      value: 'wishlist',
      label: WISHLIST_META.label,
      icon: WISHLIST_META.icon,
      count: favoriteCountByStatus.wishlist > 0 ? favoriteCountByStatus.wishlist : null,
    },
    {
      value: 'reserved',
      label: RESERVED_META.label,
      icon: RESERVED_META.icon,
      count: favoriteCountByStatus.reserved > 0 ? favoriteCountByStatus.reserved : null,
    },
  ] satisfies readonly SwipeTabSpec<LibraryTab>[]

  const renderPanel = (tab: LibraryTab) =>
    tab === 'search' ? (
      <SearchResultsPanel
        expandedBookIds={expandedBookIdSet}
        onToggle={handleToggle}
        onOpenSort={() => setSortSheetOpen(true)}
        onOpenFilter={() => setFilterSheetOpen(true)}
      />
    ) : (
      <FavoriteBooksPanel status={tab} expandedBookIds={expandedBookIdSet} onToggle={handleToggle} />
    )

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Header
        title="図書館を検索"
        onClose={() => navigate(PATHS.HOME)}
        dropdownSections={[
          {
            type: 'action',
            label: '追加',
            items: [
              {
                key: 'amazon',
                label: 'Amazon から追加',
                icon: <BookPlus size={18} />,
                onClick: () => openAmazonSheet(''),
              },
            ],
          },
          {
            type: 'action',
            label: '表示',
            items: [
              {
                key: 'history',
                label: `${RETURNED_META.label} (${favoriteCountByStatus.returned})`,
                icon: <RETURNED_META.icon size={18} />,
                onClick: () => setHistoryOpen(true),
              },
            ],
          },
          {
            type: 'action',
            label: '設定',
            items: [
              {
                key: 'todo-format',
                label: 'TODO のフォーマット',
                icon: <ListPlus size={18} />,
                onClick: () => setTodoFormatOpen(true),
              },
            ],
          },
        ]}
      />

      <div className="shrink-0 p-4 pb-2">
        <SearchForm onSearch={() => setActiveTab('search')} onAmazonUrl={openAmazonSheet} />
      </div>

      <SwipeTabs<LibraryTab>
        idPrefix={TAB_ID_PREFIX}
        ariaLabel="検索結果と候補・予約中の切り替え"
        tabs={tabs}
        activeValue={activeTab}
        onChange={setActiveTab}
        renderPanel={renderPanel}
        panelClassName="px-4 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))]"
      />

      <LibraryFilterSheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen} />
      <LibrarySortSheet open={sortSheetOpen} onOpenChange={setSortSheetOpen} />
      <HistorySheet
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        expandedBookIds={expandedBookIdSet}
        onToggle={handleToggle}
      />
      <BookTodoFormatSheet open={todoFormatOpen} onOpenChange={setTodoFormatOpen} />
      <AmazonAddSheet
        key={amazonSeed.key}
        open={amazonOpen}
        onOpenChange={setAmazonOpen}
        initialUrl={amazonSeed.url}
        onAdded={() => setActiveTab('wishlist')}
      />
    </div>
  )
}
